import {
  Box,
  Button,
  Flex,
  IconButton,
  SelectMenu,
  TextInput,
  Tooltip,
  Typography,
} from '@fullstory/components';
import { intl } from '@fullstory/intl';
import * as protobuf from '@fullstory/proto/google/protobuf';
import { fromProtoDuration, toProtoDuration } from '@fullstory/search-converter';
import { DurationUnit } from '@fullstory/search-types';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { DisabledEventToggle } from '../DisabledEventToggle';
import { TWO_EVENTS_REQUIRED_TEXT, TWO_OCCURRENCES_REQUIRED_TEXT } from '../util';
import css from './index.module.scss';

const defaultUnits = [
  DurationUnit.second,
  DurationUnit.minute,
  DurationUnit.hour,
  DurationUnit.day,
];

const WITHIN_ACTION_LABEL = intl.formatMessage({
  defaultMessage: 'within...',
  id: 'a8L6TN',
});

type WithinTimeContext = 'constraint' | 'occurrences';

export interface WithinTimeConstraintProps {
  context: WithinTimeContext;
  withinTime: protobuf.duration;
  onChange: (c: protobuf.duration) => void;
  disabled?: boolean;
  units?: DurationUnit[];
}

export const WithinTimeConstraint = ({
  context,
  withinTime,
  onChange,
  disabled = false,
  units = defaultUnits,
}: WithinTimeConstraintProps) => {
  const withinDuration = fromProtoDuration(withinTime, DurationUnit.second);
  const [scalar, setScalar] = React.useState(withinDuration.scalar ?? 0);
  const [unit, setUnit] = React.useState(scalar ? withinDuration.unit : DurationUnit.minute);
  const [endsWithDecimal, setEndsWithDecimal] = React.useState(false);
  const [needsChange, setNeedsChange] = React.useState(false);
  const [isOpen, setIsOpen] = React.useState(scalar > 0);
  const [shouldFocus, setShouldFocus] = React.useState(false);
  const [interacting, setInteracting] = React.useState(false);
  const ref = React.createRef<HTMLInputElement>();

  React.useEffect(
    () => {
      const existing = toProtoDuration({ scalar, unit });
      if (withinTime != existing) {
        const withinDuration = fromProtoDuration(withinTime, DurationUnit.second);
        const newScalar = withinDuration.scalar ?? 0;
        setScalar(newScalar);
        setUnit(newScalar ? withinDuration.unit : unit);
        setIsOpen(newScalar > 0);
      }
    },
    // intentionally not including scalar and unit in effect deps so that they don't trigger updates
    // we only want to update from external changes if it meaningfully changes the duration (regardless of unit)
    [withinTime, setScalar, setUnit, setIsOpen],
  );

  React.useEffect(() => {
    if (needsChange) {
      const changed = toProtoDuration({ scalar, unit });
      if (changed) {
        onChange(changed);
      }
      setNeedsChange(false);
    }
  }, [needsChange, scalar, unit, onChange]);

  React.useEffect(() => {
    if (isOpen && shouldFocus) {
      ref.current?.focus();
      setShouldFocus(false);
    }
  }, [isOpen]);

  const durations = React.useMemo(
    () =>
      [
        {
          key: DurationUnit.milli,
          value: DurationUnit.milli,
          label: intl.formatMessage(
            {
              defaultMessage: '{scalar, plural, one {millisecond} other {milliseconds}}',
              id: 'Cb8R8S',
            },
            { scalar },
          ),
        },
        {
          key: DurationUnit.second,
          value: DurationUnit.second,
          label: intl.formatMessage(
            { defaultMessage: '{scalar, plural, one {second} other {seconds}}', id: 'vuNZ3E' },
            { scalar },
          ),
        },
        {
          key: DurationUnit.minute,
          value: DurationUnit.minute,
          label: intl.formatMessage(
            { defaultMessage: '{scalar, plural, one {minute} other {minutes}}', id: 'w4P1jS' },
            { scalar },
          ),
        },
        {
          key: DurationUnit.hour,
          value: DurationUnit.hour,
          label: intl.formatMessage(
            { defaultMessage: '{scalar, plural, one {hour} other {hours}}', id: 'uJrtJL' },
            { scalar },
          ),
        },
        {
          key: DurationUnit.day,
          value: DurationUnit.day,
          label: intl.formatMessage(
            { defaultMessage: '{scalar, plural, one {day} other {days}}', id: 'bFNKh1' },
            { scalar },
          ),
        },
      ].filter(({ key }) => units.includes(key)),
    [scalar, units],
  );

  return (
    <Flex onMouseEnter={() => setInteracting(true)} onMouseLeave={() => setInteracting(false)}>
      {disabled ? (
        <DisabledEventToggle
          label={WITHIN_ACTION_LABEL}
          tooltipText={
            context === 'constraint' ? TWO_EVENTS_REQUIRED_TEXT : TWO_OCCURRENCES_REQUIRED_TEXT
          }
        />
      ) : !isOpen ? (
        <Tooltip
          variant="light"
          placement="bottom"
          tooltipContent={
            <Typography variant="body3">
              {context === 'constraint' ? (
                <FormattedMessage
                  defaultMessage="Set a maximum time that all events must occur within"
                  id="YYL/rO"
                  description="Tooltip for within time constraint, when used in main constraints"
                />
              ) : (
                <FormattedMessage
                  defaultMessage="Set a maximum time that all occurrences of this event must occur within"
                  id="UEUt7L"
                  description="Tooltip for within time constraint, when used with occurrences"
                />
              )}
            </Typography>
          }
        >
          <Button
            variant="underline-builder"
            size="x-small"
            onClick={() => {
              setShouldFocus(true);
              setIsOpen(true);
            }}
          >
            {WITHIN_ACTION_LABEL}
          </Button>
        </Tooltip>
      ) : (
        <>
          <FormattedMessage
            defaultMessage="within"
            id="Uhfqhu"
            description="Used to indicate within an amount of time"
          />
          <Box marginLeft={8} width={48}>
            <TextInput
              ref={ref}
              className={css.withinNumericInput}
              testId="within-time-constraint-input"
              size="x-small"
              min={0}
              variant="minimal"
              value={(scalar <= 0 ? '' : scalar.toString()) + (endsWithDecimal ? '.' : '')}
              onChange={e => {
                setNeedsChange(false);
                if (e.target.value === '') {
                  setScalar(0);
                  return;
                }
                const parsed = parseFloat(e.target.value);
                if (!isNaN(parsed)) {
                  setScalar(parsed);
                  setEndsWithDecimal(
                    e.target.value.endsWith('.') && e.target.value.split('.').length == 2,
                  );
                }
              }}
              onBlur={() => {
                if (scalar >= 0) {
                  // valid value, let's commit change
                  setNeedsChange(true);
                  if (scalar === 0 && !interacting) {
                    // but if 0 and the user is not interacting, let's also close the constraint select
                    setIsOpen(false);
                  }
                }
              }}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  ref.current?.blur();
                }
              }}
            />
          </Box>
          <Box marginLeft={8}>
            <SelectMenu
              variant="underline-builder"
              size="x-small"
              selected={durations.find(o => o.value === unit) ?? null}
              onChange={o => {
                if (o) {
                  setUnit(o.value);
                  setNeedsChange(true);
                }
              }}
              options={durations}
            />
          </Box>
          {context === 'constraint' && (
            <Box marginLeft={8}>
              <IconButton
                title={intl.formatMessage({
                  defaultMessage: 'Remove within constraint',
                  id: 'hH0wT8',
                })}
                iconName="close"
                variant="minimal"
                onClick={() => {
                  setScalar(0);
                  setNeedsChange(true);
                  setIsOpen(false);
                }}
              />
            </Box>
          )}
        </>
      )}
    </Flex>
  );
};
